// Portal System Types and Mock Data
// Shared between Client Portal and Internal Dashboard

export type RFQStatus = 'submitted' | 'under-review' | 'validation-completed' | 'proposal-generated' | 'pending-feedback' | 'closed';
export type Priority = 'high' | 'medium' | 'low';
export type FeedbackStatus = 'accepted' | 'revision-requested' | 'no-response' | null;
export type ClientType = 'operator' | 'epc' | 'vendor';

export interface PortalRFQ {
  id: string;
  service: string;
  serviceLabel: string;
  status: RFQStatus;
  priority: Priority;
  date: string;
  client: string;
  company: string;
  email: string;
  country: string;
  countryLabel: string;
  clientType: ClientType;
  environment: string;
  wellheadPressure: string;
  oilRate: string;
  gasRate: string;
  h2sPresence: string;
  sandProduction: string;
  serviceModel: string;
  duration: string;
  timeline: string;
  locationType: string;
  notes: string;
  equipmentCount: number;
  flags: string[];
  validationStatus: 'Approved' | 'Conditional' | 'Rejected' | null;
  proposalGenerated: boolean;
  lastUpdated: string;
  // New fields for Command Center
  assignedEngineer: string | null;
  assignedCommercial: string | null;
  feedbackStatus: FeedbackStatus;
  nextStep: string;
}

export interface ActivityEntry {
  id: string;
  rfqId: string;
  action: string;
  actor: string;
  actorRole: 'system' | 'client' | 'engineer';
  timestamp: string;
  details?: string;
}

export interface RevisionRequest {
  id: string;
  rfqId: string;
  clientName: string;
  date: string;
  comments: string;
  status: 'pending' | 'reviewed' | 'implemented';
}

export interface EngineeringNote {
  id: string;
  rfqId: string;
  author: string;
  date: string;
  note: string;
  type: 'approval' | 'modification' | 'rejection' | 'general';
}

// Status display config
export const STATUS_CONFIG: Record<RFQStatus, { label: string; color: string; bgColor: string; borderColor: string }> = {
  'submitted': { label: 'Submitted', color: 'text-blue-300', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
  'under-review': { label: 'Under Review', color: 'text-amber-300', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
  'validation-completed': { label: 'Validation Completed', color: 'text-purple-300', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30' },
  'proposal-generated': { label: 'Proposal Generated', color: 'text-green-300', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30' },
  'pending-feedback': { label: 'Pending Feedback', color: 'text-orange-300', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30' },
  'closed': { label: 'Closed', color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/30' },
};

export const PRIORITY_CONFIG: Record<Priority, { label: string; color: string; bgColor: string; borderColor: string }> = {
  high: { label: 'High', color: 'text-red-300', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30' },
  medium: { label: 'Medium', color: 'text-amber-300', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
  low: { label: 'Low', color: 'text-green-300', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30' },
};

// Next step message per status
export const NEXT_STEP_MAP: Record<RFQStatus, string> = {
  'submitted': 'Your RFQ is in the queue. An engineer will be assigned shortly.',
  'under-review': 'Engineering team is reviewing your technical requirements.',
  'validation-completed': 'Technical validation complete. Proposal is being prepared.',
  'proposal-generated': 'Proposal is ready for your review. Please provide feedback.',
  'pending-feedback': 'Awaiting your response on the submitted proposal.',
  'closed': 'This RFQ is closed. Contact us to re-open or submit a new request.',
};

// Engineers and commercial staff
export const ENGINEERS = ['Eng. Mohammed Hassan', 'Eng. Faisal Ahmed', 'Eng. Sara Al-Dosari', 'Eng. Omar Khalil'];
export const COMMERCIAL_OWNERS = ['Abdulrahman Saeed', 'James Williams', 'Layla Mansouri'];

// Mock RFQ Data
export const MOCK_RFQS: PortalRFQ[] = [
  {
    id: 'EP-2N4K8M',
    service: 'surface-well-testing',
    serviceLabel: 'Surface Well Testing',
    status: 'proposal-generated',
    priority: 'high',
    date: '2026-04-28',
    client: 'Ahmed Al-Rashid',
    company: 'National Energy Corp',
    email: 'a.rashid@nec.com',
    country: 'ksa',
    countryLabel: 'Saudi Arabia',
    clientType: 'operator',
    environment: 'onshore',
    wellheadPressure: '7500',
    oilRate: '12000',
    gasRate: '35',
    h2sPresence: 'yes',
    sandProduction: 'high',
    serviceModel: 'full-service',
    duration: 'long-term',
    timeline: 'immediate',
    locationType: 'remote',
    notes: 'Require 10K-rated equipment. H2S contingency plan mandatory.',
    equipmentCount: 14,
    flags: ['10K PSI equipment required', 'H₂S safety systems mandatory', 'Full sand management system'],
    validationStatus: 'Conditional',
    proposalGenerated: true,
    lastUpdated: '2026-04-30T14:30:00Z',
    assignedEngineer: 'Eng. Mohammed Hassan',
    assignedCommercial: 'Abdulrahman Saeed',
    feedbackStatus: 'no-response',
    nextStep: 'Awaiting client feedback on technical proposal.',
  },
  {
    id: 'EP-3F7R2P',
    service: 'stimulation-nitrogen',
    serviceLabel: 'Stimulation & Nitrogen',
    status: 'under-review',
    priority: 'medium',
    date: '2026-04-29',
    client: 'James Patterson',
    company: 'Gulf Petroleum Services',
    email: 'j.patterson@gps.ae',
    country: 'uae',
    countryLabel: 'UAE',
    clientType: 'epc',
    environment: 'onshore',
    wellheadPressure: '4200',
    oilRate: '8000',
    gasRate: '15',
    h2sPresence: 'no',
    sandProduction: 'low',
    serviceModel: 'full-service',
    duration: 'short-term',
    timeline: '1-2-weeks',
    locationType: 'city',
    notes: 'Standard stimulation scope for 3-well program.',
    equipmentCount: 8,
    flags: [],
    validationStatus: null,
    proposalGenerated: false,
    lastUpdated: '2026-04-29T09:15:00Z',
    assignedEngineer: 'Eng. Faisal Ahmed',
    assignedCommercial: 'James Williams',
    feedbackStatus: null,
    nextStep: 'Engineering validation in progress.',
  },
  {
    id: 'EP-5H9T1W',
    service: 'fabrication-skid',
    serviceLabel: 'Fabrication & Skid Assembly',
    status: 'pending-feedback',
    priority: 'high',
    date: '2026-04-25',
    client: 'Khalid Mansour',
    company: 'EPTCO Engineering',
    email: 'k.mansour@eptco.com',
    country: 'egypt',
    countryLabel: 'Egypt',
    clientType: 'epc',
    environment: 'onshore',
    wellheadPressure: '',
    oilRate: '',
    gasRate: '',
    h2sPresence: 'no',
    sandProduction: '',
    serviceModel: 'full-service',
    duration: 'long-term',
    timeline: 'planned',
    locationType: 'city',
    notes: '12 skid packages for production facility. ASME certification required.',
    equipmentCount: 5,
    flags: ['Custom fabrication scope', 'ASME certification required'],
    validationStatus: 'Approved',
    proposalGenerated: true,
    lastUpdated: '2026-04-28T16:45:00Z',
    assignedEngineer: 'Eng. Sara Al-Dosari',
    assignedCommercial: 'Layla Mansouri',
    feedbackStatus: 'revision-requested',
    nextStep: 'Revision request under review by engineering.',
  },
  {
    id: 'EP-7K2M4X',
    service: 'logistics-transport',
    serviceLabel: 'Logistics & Transportation',
    status: 'submitted',
    priority: 'low',
    date: '2026-04-30',
    client: 'Omar Nasser',
    company: 'Desert Operations LLC',
    email: 'o.nasser@desertops.com',
    country: 'ksa',
    countryLabel: 'Saudi Arabia',
    clientType: 'vendor',
    environment: 'onshore',
    wellheadPressure: '',
    oilRate: '',
    gasRate: '',
    h2sPresence: 'no',
    sandProduction: '',
    serviceModel: 'rental',
    duration: 'short-term',
    timeline: '1-2-weeks',
    locationType: 'desert',
    notes: 'Heavy transport for equipment relocation — 8 loads.',
    equipmentCount: 3,
    flags: ['Remote logistics'],
    validationStatus: null,
    proposalGenerated: false,
    lastUpdated: '2026-04-30T08:00:00Z',
    assignedEngineer: null,
    assignedCommercial: null,
    feedbackStatus: null,
    nextStep: 'Pending engineer assignment.',
  },
  {
    id: 'EP-9N1Q6Z',
    service: 'chemicals-supply',
    serviceLabel: 'Chemicals Supply',
    status: 'closed',
    priority: 'medium',
    date: '2026-04-20',
    client: 'Sarah Mitchell',
    company: 'Apex Energy Solutions',
    email: 's.mitchell@apex.com',
    country: 'uae',
    countryLabel: 'UAE',
    clientType: 'operator',
    environment: 'offshore',
    wellheadPressure: '',
    oilRate: '5000',
    gasRate: '',
    h2sPresence: 'yes',
    sandProduction: 'medium',
    serviceModel: 'full-service',
    duration: 'long-term',
    timeline: 'planned',
    locationType: 'remote',
    notes: 'Corrosion inhibitor program for offshore platform.',
    equipmentCount: 4,
    flags: ['H₂S scavenger required', 'Offshore logistics'],
    validationStatus: 'Approved',
    proposalGenerated: true,
    lastUpdated: '2026-04-26T11:20:00Z',
    assignedEngineer: 'Eng. Omar Khalil',
    assignedCommercial: 'Abdulrahman Saeed',
    feedbackStatus: 'accepted',
    nextStep: 'Proposal accepted. Project handed over to operations.',
  },
];

// Mock Activity Timeline
export const MOCK_ACTIVITIES: ActivityEntry[] = [
  { id: 'act-1', rfqId: 'EP-2N4K8M', action: 'RFQ Submitted', actor: 'Ahmed Al-Rashid', actorRole: 'client', timestamp: '2026-04-28T08:30:00Z' },
  { id: 'act-2', rfqId: 'EP-2N4K8M', action: 'Assigned to Engineering Review', actor: 'System', actorRole: 'system', timestamp: '2026-04-28T08:31:00Z' },
  { id: 'act-3', rfqId: 'EP-2N4K8M', action: 'Engineering Validation Completed', actor: 'System', actorRole: 'system', timestamp: '2026-04-29T10:00:00Z', details: 'Status: Conditional — 2 items flagged' },
  { id: 'act-4', rfqId: 'EP-2N4K8M', action: 'Technical Proposal Generated', actor: 'Eng. Mohammed Hassan', actorRole: 'engineer', timestamp: '2026-04-30T14:30:00Z' },
  { id: 'act-5', rfqId: 'EP-3F7R2P', action: 'RFQ Submitted', actor: 'James Patterson', actorRole: 'client', timestamp: '2026-04-29T09:15:00Z' },
  { id: 'act-6', rfqId: 'EP-3F7R2P', action: 'Under Engineering Review', actor: 'Eng. Faisal Ahmed', actorRole: 'engineer', timestamp: '2026-04-29T11:00:00Z' },
  { id: 'act-7', rfqId: 'EP-5H9T1W', action: 'Proposal Sent to Client', actor: 'System', actorRole: 'system', timestamp: '2026-04-28T16:45:00Z' },
  { id: 'act-8', rfqId: 'EP-5H9T1W', action: 'Revision Request Received', actor: 'Khalid Mansour', actorRole: 'client', timestamp: '2026-04-29T10:00:00Z' },
];

export const MOCK_REVISION_REQUESTS: RevisionRequest[] = [
  { id: 'rev-1', rfqId: 'EP-5H9T1W', clientName: 'Khalid Mansour', date: '2026-04-29T10:00:00Z', comments: 'Please confirm ASME U-stamp availability for all pressure vessels. Also need updated delivery timeline if we add 4 additional skids.', status: 'pending' },
];

export const MOCK_ENGINEERING_NOTES: EngineeringNote[] = [
  { id: 'note-1', rfqId: 'EP-2N4K8M', author: 'Eng. Mohammed Hassan', date: '2026-04-29T10:30:00Z', note: 'H2S safety systems confirmed available in Al Khobar yard. NACE materials in stock. 10K equipment reserved.', type: 'general' },
  { id: 'note-2', rfqId: 'EP-2N4K8M', author: 'Eng. Mohammed Hassan', date: '2026-04-30T14:00:00Z', note: 'Proposal approved for release. Client advised on conditional items — sand management removal carries elevated erosion risk.', type: 'approval' },
  { id: 'note-3', rfqId: 'EP-3F7R2P', author: 'Eng. Faisal Ahmed', date: '2026-04-29T11:30:00Z', note: 'Standard scope. Reviewing nitrogen unit availability for requested timeline.', type: 'general' },
];
